import "core-js/modules/es.array.reverse.js";
var _class, _class2, _WebFeatureServiceCatalogItem;
import "core-js/modules/es.regexp.constructor.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/web.dom-collections.iterator.js";
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
import i18next from "i18next";
import { computed, makeObservable, override, runInAction } from "mobx";
import combine from "terriajs-cesium/Source/Core/combine";
import { toFeatureCollection } from "../../../Core/GeoJson";
import TerriaError from "../../../Core/TerriaError";
import containsAny from "../../../Core/containsAny";
import isDefined from "../../../Core/isDefined";
import isReadOnlyArray from "../../../Core/isReadOnlyArray";
import loadText from "../../../Core/loadText";
import gmlToGeoJson from "../../../Map/Vector/gmlToGeoJson";
import { getName } from "../../../ModelMixins/CatalogMemberMixin";
import GeoJsonMixin from "../../../ModelMixins/GeojsonMixin";
import GetCapabilitiesMixin from "../../../ModelMixins/GetCapabilitiesMixin";
import xml2json from "../../../ThirdParty/xml2json";
import { InfoSectionTraits } from "../../../Traits/TraitsClasses/CatalogMemberTraits";
import WebFeatureServiceCatalogItemTraits, { SUPPORTED_CRS_3857, SUPPORTED_CRS_4326 } from "../../../Traits/TraitsClasses/WebFeatureServiceCatalogItemTraits";
import CreateModel from "../../Definition/CreateModel";
import LoadableStratum from "../../Definition/LoadableStratum";
import createStratumInstance from "../../Definition/createStratumInstance";
import proxyCatalogItemUrl from "../proxyCatalogItemUrl";
import WebFeatureServiceCapabilities, { getRectangleFromLayer } from "./WebFeatureServiceCapabilities";
export let GetCapabilitiesStratum = (_class = class GetCapabilitiesStratum extends LoadableStratum(WebFeatureServiceCatalogItemTraits) {
  static async load(catalogItem, capabilities) {
    if (!isDefined(catalogItem.getCapabilitiesUrl)) {
      throw new TerriaError({
        title: i18next.t("models.webFeatureServiceCatalogItem.missingUrlTitle"),
        message: i18next.t("models.webFeatureServiceCatalogItem.missingUrlMessage")
      });
    }
    if (!isDefined(capabilities)) capabilities = await WebFeatureServiceCapabilities.fromUrl(proxyCatalogItemUrl(catalogItem, catalogItem.getCapabilitiesUrl, catalogItem.getCapabilitiesCacheDuration));
    return new GetCapabilitiesStratum(catalogItem, capabilities);
  }
  constructor(catalogItem, capabilities) {
    super();
    this.catalogItem = catalogItem;
    this.capabilities = capabilities;
    // Helper function to check if geojson output is supported (by checking GetCapabilities OutputTypes OR FeatureType OutputTypes)
    _defineProperty(this, "hasJsonOutputFormat", outputFormats => {
      return isDefined(outputFormats === null || outputFormats === void 0 ? void 0 : outputFormats.find(format => ["json", "JSON", "application/json"].includes(format)));
    });
    makeObservable(this);
  }
  duplicateLoadableStratum(model) {
    return new GetCapabilitiesStratum(model, this.capabilities);
  }
  get capabilitiesFeatureTypes() {
    const lookup = name => [name, this.capabilities && this.capabilities.findLayer(name)];
    return new Map(this.catalogItem.typeNamesArray.map(lookup));
  }
  get info() {
    const result = [createStratumInstance(InfoSectionTraits, {
      name: i18next.t("models.webFeatureServiceCatalogItem.getCapabilitiesUrl"),
      content: this.catalogItem.getCapabilitiesUrl
    })];
    let firstDataDescription;
    for (const layer of this.capabilitiesFeatureTypes.values()) {
      if (!layer || !layer.Abstract || containsAny(layer.Abstract, WebFeatureServiceCatalogItem.abstractsToIgnore)) {
        continue;
      }
      const suffix = this.capabilitiesFeatureTypes.size === 1 ? "" : " - ".concat(layer.Title);
      const name = "".concat(i18next.t("models.webFeatureServiceCatalogItem.abstract")).concat(suffix);
      result.push(createStratumInstance(InfoSectionTraits, {
        name,
        content: layer.Abstract
      }));
      firstDataDescription = firstDataDescription || layer.Abstract;
    }

    // Show the service abstract if there is one and if it isn't the Geoserver default "A compliant implementation..."
    const service = this.capabilities && this.capabilities.service;
    if (service) {
      if (service && service.Abstract && !containsAny(service.Abstract, WebFeatureServiceCatalogItem.abstractsToIgnore) && service.Abstract !== firstDataDescription) {
        result.push(createStratumInstance(InfoSectionTraits, {
          name: i18next.t("models.webFeatureServiceCatalogItem.abstract"),
          content: service.Abstract
        }));
      }

      // Show the Access Constraints if it isn't "none" (because that's the default, and usually a lie).
      if (service.AccessConstraints && !/^none$/i.test(service.AccessConstraints)) {
        result.push(createStratumInstance(InfoSectionTraits, {
          name: i18next.t("models.webFeatureServiceCatalogItem.accessConstraints"),
          content: service.AccessConstraints
        }));
      }
    }
    return result;
  }
  get infoSectionOrder() {
    let layerDescriptions = [i18next.t("models.webFeatureServiceCatalogItem.abstract")];

    // If more than one layer, push layer description titles for each applicable layer
    if (this.capabilitiesFeatureTypes.size > 1) {
      layerDescriptions = [];
      this.capabilitiesFeatureTypes.forEach(layer => {
        if (layer && layer.Abstract && !containsAny(layer.Abstract, WebFeatureServiceCatalogItem.abstractsToIgnore)) {
          layerDescriptions.push("".concat(i18next.t("models.webFeatureServiceCatalogItem.abstract"), " - ").concat(layer.Title));
        }
      });
    }
    return [i18next.t("preview.disclaimer"), i18next.t("description.name"), ...layerDescriptions, i18next.t("preview.datasetDescription"), i18next.t("preview.serviceDescription"), i18next.t("models.webFeatureServiceCatalogItem.serviceDescription"), i18next.t("preview.resourceDescription"), i18next.t("preview.licence"), i18next.t("preview.accessConstraints"), i18next.t("models.webFeatureServiceCatalogItem.accessConstraints"), i18next.t("preview.author"), i18next.t("preview.contact"), i18next.t("models.webFeatureServiceCatalogItem.serviceContact"), i18next.t("preview.created"), i18next.t("preview.modified"), i18next.t("preview.updateFrequency"), i18next.t("models.webFeatureServiceCatalogItem.getCapabilitiesUrl")];
  }
  get rectangle() {
    const layers = [...this.capabilitiesFeatureTypes.values()].filter(isDefined);

    // Only return first layer's rectangle - as we don't support multiple WFS layers
    return layers.length > 0 ? getRectangleFromLayer(layers[0]) : undefined;
  }
  get isGeoServer() {
    if (!this.capabilities) {
      return undefined;
    }
    if (!this.capabilities.service || !this.capabilities.service.KeywordList || !this.capabilities.service.KeywordList.Keyword) {
      return false;
    }
    const keyword = this.capabilities.service.KeywordList.Keyword;
    if (isReadOnlyArray(keyword)) {
      return keyword.indexOf("GEOSERVER") >= 0;
    } else {
      return keyword === "GEOSERVER";
    }
  }
  // Find which GML formats are supported, choose the one most suited to Terria. If not available, default to "gml3"
  get outputFormat() {
    var _this$capabilities$ou, _this$capabilities$ou2;
    const supportsGeojson = this.hasJsonOutputFormat(this.capabilities.outputTypes) || [...this.capabilitiesFeatureTypes.values()].reduce((hasGeojson, current) => hasGeojson && this.hasJsonOutputFormat(current === null || current === void 0 ? void 0 : current.OutputFormats), true);
    const searchValue = new RegExp(".*gml/3.1.1.*|.*gml3.1.1.*");
    return supportsGeojson ? "JSON" : (_this$capabilities$ou = (_this$capabilities$ou2 = this.capabilities.outputTypes) === null || _this$capabilities$ou2 === void 0 ? void 0 : _this$capabilities$ou2.find(outputFormat => searchValue.test(outputFormat))) !== null && _this$capabilities$ou !== void 0 ? _this$capabilities$ou : "gml3";
  }

  /** Finds the best srsName to use.
   * First checks if one provided in url. If one is provided in url, and this is supported by Terria, will use this.
   * Note that an error will be thrown if user supplied srsName is not supported by the user supplied WFS service.
   * If no srsName provided, or the provided one is not supported by Terria,
   * then checks getCapabilities response and returns the first listed srs that is included in our list of supported srs.
   * This enables us to use a urn identifier if supported, or a normal EPSG code if not.
   * e.g. "urn:ogc:def:crs:EPSG::4326" or "EPSG:4326"
   **/
  get srsName() {
    var _this$catalogItem$uri, _this$catalogItem$uri2, _ref, _ref2, _ref3, _queryParams$srsName, _this$capabilities$sr, _layerSrsArray$srsArr;
    // First check to see if URL has CRS or SRS
    const supportedCrs = [...SUPPORTED_CRS_3857, ...SUPPORTED_CRS_4326];
    const queryParams = (_this$catalogItem$uri = (_this$catalogItem$uri2 = this.catalogItem.uri) === null || _this$catalogItem$uri2 === void 0 ? void 0 : _this$catalogItem$uri2.query(true)) !== null && _this$catalogItem$uri !== void 0 ? _this$catalogItem$uri : {};
    const urlCrs = (_ref = (_ref2 = (_ref3 = (_queryParams$srsName = queryParams.srsName) !== null && _queryParams$srsName !== void 0 ? _queryParams$srsName : queryParams.crs) !== null && _ref3 !== void 0 ? _ref3 : queryParams.CRS) !== null && _ref2 !== void 0 ? _ref2 : queryParams.srs) !== null && _ref !== void 0 ? _ref : queryParams.SRS;
    if (urlCrs && supportedCrs.includes(urlCrs)) return urlCrs;

    // If no srsName provided, then find what the server supports and use the best one for Terria
    const layerSrsArray = (_this$capabilities$sr = this.capabilities.srsNames) === null || _this$capabilities$sr === void 0 ? void 0 : _this$capabilities$sr.find(layer => layer.layerName === this.catalogItem.typeNamesArray[0] //If multiple layers in this WFS request, only use the first layer to find best srsName
    );
    return (_layerSrsArray$srsArr = layerSrsArray === null || layerSrsArray === void 0 ? void 0 : layerSrsArray.srsArray.find(srsName => SUPPORTED_CRS_4326.includes(srsName))) !== null && _layerSrsArray$srsArr !== void 0 ? _layerSrsArray$srsArr : "urn:ogc:def:crs:EPSG::4326" // Default to urn identifier for WGS84 if we cant find something better. Sometimes WFS service will support this even if not specified in GetCapabilities response.
    ;
  }
}, _applyDecoratedDescriptor(_class.prototype, "capabilitiesFeatureTypes", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "capabilitiesFeatureTypes"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "info", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "info"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "infoSectionOrder", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "infoSectionOrder"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "rectangle", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "rectangle"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isGeoServer", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isGeoServer"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "outputFormat", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "outputFormat"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "srsName", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "srsName"), _class.prototype), _class);
let WebFeatureServiceCatalogItem = (_class2 = (_WebFeatureServiceCatalogItem = class WebFeatureServiceCatalogItem extends GetCapabilitiesMixin(GeoJsonMixin(CreateModel(WebFeatureServiceCatalogItemTraits))) {
  constructor() {
    super(...arguments);
    // hide elements in the info section which might show information about the datasource
    _defineProperty(this, "_sourceInfoItemNames", [i18next.t("models.webFeatureServiceCatalogItem.getCapabilitiesUrl")]);
    makeObservable(this);
  }
  get type() {
    return WebFeatureServiceCatalogItem.type;
  }
  get defaultGetCapabilitiesUrl() {
    if (this.uri) {
      return this.uri.clone().setSearch({
        service: "WFS",
        version: "1.1.0",
        request: "GetCapabilities"
      }).toString();
    } else {
      return undefined;
    }
  }
  async createGetCapabilitiesStratumFromParent(capabilities) {
    const stratum = await GetCapabilitiesStratum.load(this, capabilities);
    runInAction(() => {
      this.strata.set(GetCapabilitiesMixin.getCapabilitiesStratumName, stratum);
    });
  }
  async forceLoadMetadata() {
    if (this.strata.get(GetCapabilitiesMixin.getCapabilitiesStratumName) !== undefined) return;
    const stratum = await GetCapabilitiesStratum.load(this);
    runInAction(() => {
      this.strata.set(GetCapabilitiesMixin.getCapabilitiesStratumName, stratum);
    });
  }
  async forceLoadGeojsonData() {
    const getCapabilitiesStratum = this.strata.get(GetCapabilitiesMixin.getCapabilitiesStratumName);
    if (!this.uri) {
      throw new TerriaError({
        sender: this,
        title: i18next.t("models.webFeatureServiceCatalogItem.missingUrlTitle"),
        message: i18next.t("models.webFeatureServiceCatalogItem.missingUrlMessage", this)
      });
    }

    // Check if layers exist
    const missingLayers = this.typeNamesArray.filter(layer => !isDefined(getCapabilitiesStratum.capabilitiesFeatureTypes.get(layer)));
    if (missingLayers.length > 0) {
      throw new TerriaError({
        sender: this,
        title: i18next.t("models.webFeatureServiceCatalogItem.noLayerFoundTitle"),
        message: i18next.t("models.webFeatureServiceCatalogItem.noLayerFoundMessage", {
          name: getName(this),
          typeNames: missingLayers.join(", ")
        })
      });
    }
    const url = this.uri.clone().setSearch(combine({
      service: "WFS",
      request: "GetFeature",
      typeName: this.typeNames,
      version: "1.1.0",
      outputFormat: this.outputFormat,
      srsName: this.srsName,
      maxFeatures: this.maxFeatures
    }, this.parameters)).toString();
    const getFeatureResponse = await loadText(proxyCatalogItemUrl(this, url));

    // Check for errors (if supportsGeojson and the request returns XML, OR the response includes ExceptionReport)
    if (this.outputFormat === "JSON" && getFeatureResponse.startsWith("<") || getFeatureResponse.includes("ExceptionReport")) {
      let errorMessage;
      try {
        var _xml2json$Exception;
        errorMessage = (_xml2json$Exception = xml2json(getFeatureResponse).Exception) === null || _xml2json$Exception === void 0 ? void 0 : _xml2json$Exception.ExceptionText;
      } catch (_unused) {}
      const originalError = isDefined(errorMessage) ? new TerriaError({
        sender: this,
        title: "Exception from service",
        message: errorMessage
      }) : undefined;
      throw new TerriaError({
        sender: this,
        title: i18next.t("models.webFeatureServiceCatalogItem.missingDataTitle"),
        message: "".concat(i18next.t("models.webFeatureServiceCatalogItem.missingDataMessage", {
          name: getName(this)
        })),
        originalError
      });
    }
    const geojsonData = this.outputFormat === "JSON" ? JSON.parse(getFeatureResponse) : gmlToGeoJson(getFeatureResponse);
    const fc = toFeatureCollection(geojsonData);
    if (fc) return fc;
    throw TerriaError.from("Invalid geojson data - only FeatureCollection and Feature are supported");
  }
  get typeNamesArray() {
    if (Array.isArray(this.typeNames)) {
      return this.typeNames;
    } else if (this.typeNames) {
      return this.typeNames.split(",");
    } else {
      return [];
    }
  }
  get shortReport() {
    var _this$readyData;
    // Show notice if reached
    if (((_this$readyData = this.readyData) === null || _this$readyData === void 0 ? void 0 : _this$readyData.features) !== undefined && this.readyData.features.length >= this.maxFeatures) {
      return i18next.t("models.webFeatureServiceCatalogItem.reachedMaxFeatureLimit", this);
    }
    return undefined;
  }
}, _defineProperty(_WebFeatureServiceCatalogItem, "abstractsToIgnore", ["A compliant implementation of WFS", "This is the reference implementation of WFS 1.0.0 and WFS 1.1.0, supports all WFS operations including Transaction."]), _defineProperty(_WebFeatureServiceCatalogItem, "type", "wfs"), _WebFeatureServiceCatalogItem), _applyDecoratedDescriptor(_class2.prototype, "typeNamesArray", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "typeNamesArray"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "shortReport", [override], Object.getOwnPropertyDescriptor(_class2.prototype, "shortReport"), _class2.prototype), _class2);
export default WebFeatureServiceCatalogItem;